import { useContext, useEffect, useRef } from 'react';
import { store } from 'components/Store';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useLocation } from 'react-router';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';

const partyColours = {
  labour: '196, 44, 61',
  'labour-independent': '196, 44, 61',
  'independent-labour': '196, 44, 61',
  'labour-co-op': '196, 44, 61',
  'labour-co-op-': '196, 44, 61',
  conservative: '93, 156, 222',
  'conservative-independent': '93, 156, 222',
  'democratic-unionis': '0, 0, 128',
  'democratic-unionist-party': '0, 0, 128',
  dup: '0, 0, 128',
  'non-affiliated': '77, 77, 77',
  'lord-speaker': '77, 77, 77',
  speaker: '77, 77, 77',
  'liberal-democrat': '238, 197, 67',
  'lib-dems': '238, 197, 67',
  'green-party': '129, 175, 63',
  'scottish-national-party': '250, 245, 144',
  snp: '250, 245, 144',
  'social-democratic-labour-party': '63, 132, 40',
  'sinn-f-in': '153, 204, 0',
  'plaid-cymru': '63, 132, 40',
  'ulster-unionist-party': '153, 204, 255',
  democratic: '93, 156, 222',
  republican: '196, 44, 61',
  independent: '115, 215, 215',
};

const stateColours = {
  florida: '0, 33, 165',
  'north-carolina': '78, 121, 167',
  texas: '196, 44, 61',
  michigan: '93, 156, 222',
  iowa: '238, 197, 67',
  colorado: '129, 175, 63',
  illinois: '250, 245, 144',
  maryland: '63, 132, 40',
  'new-york': '153, 204, 0',
  pennsylvania: '115, 215, 215',
  virginia: '206, 17, 38',
  california: '77, 77, 77',
  idaho: '196, 44, 61',
  indiana: '93, 156, 222',
  kentucky: '238, 197, 67',
  massachusetts: '129, 175, 63',
  'new-jersey': '250, 245, 144',
  'new-mexico': '63, 132, 40',
  ohio: '153, 204, 0',
  oregon: '115, 215, 215',
  washington: '206, 17, 38',
  minnesota: '77, 77, 77',
  'new-hampshire': '196, 44, 61',
  oklahoma: '93, 156, 222',
  'south-carolina': '238, 197, 67',
  tennessee: '129, 175, 63',
  utah: '250, 245, 144',
  alabama: '63, 132, 40',
  arizona: '153, 204, 0',
  georgia: '115, 215, 215',
  hawaii: '206, 17, 38',
  kansas: '77, 77, 77',
  louisiana: '196, 44, 61',
  missouri: '93, 156, 222',
  nebraska: '238, 197, 67',
  nevada: '129, 175, 63',
  'north-dakota': '250, 245, 144',
  'rhode-island': '63, 132, 40',
};

const houseColours = {
  house: '242, 168, 5',
  senate: '0, 148, 204',
};

const getPlaceholderImageUrl = () => {
  const placeHolderUrl = `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`;
  const placeHolderMime = 'image/png';
  return `${import.meta.env.VITE_APP_DOTNETAPIURL_FOR_REPORT_IMAGES}/api/cors/resource?url=${encodeURIComponent(
    placeHolderUrl
  )}&mime=${encodeURIComponent(placeHolderMime)}&cache=true`;
};

const stakeholderUrlImage = ({ imageId, imageUrl }) => {
  const urlImage = imageUrl
    ? imageUrl
    : imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
      ? `${import.meta.env.VITE_APP_DOTNETAPIURL_FOR_REPORT_IMAGES}/api/parliament-today/image/${imageId}`
      : getPlaceholderImageUrl();
  return urlImage;
};

const useCallMostRelevantStakeholders = (props) => {
  const { teamId } = props ?? {};
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const keywordsListsToUse = keywordsLists.filter((item) => item.id !== null);

  const { generalApiCall } = useGeneralApiCall();
  const topicParameter = getUrlParam('topic-id');
  const location = useLocation();

  const mountedHook = useRef(true);
  useEffect(() => {
    return () => {
      mountedHook.current = false;
    };
  }, [location.pathname, location.search]);

  const createImageUrl = ({ imageId, imageUrl, party, usePlaceholder = false }) => {
    return new Promise((resolve, reject) => {
      const urlImage = usePlaceholder ? getPlaceholderImageUrl() : stakeholderUrlImage({ imageId, imageUrl });
      let base_image = new Image();
      base_image.crossOrigin = 'anonymous';
      base_image.src = urlImage;
      base_image.onload = () => {
        // Reject if image is 10x10 or smaller (indicates error/placeholder from server)
        if (base_image.width <= 10 && base_image.height <= 10) {
          reject(new Error('Image too small'));
          return;
        }

        let tmpCanvas = document.createElement('canvas');
        let tmpCtx = tmpCanvas.getContext('2d');
        tmpCanvas.width = 40;
        tmpCanvas.height = 40;
        tmpCtx.save();
        tmpCtx.beginPath();
        tmpCtx.arc(20, 20, 17, 0, 2 * Math.PI);
        tmpCtx.fillStyle = usePlaceholder
          ? 'rgb(213,213,212)'
          : partyColours?.[party]
            ? `rgb(${partyColours[party]})`
            : '0, 18, 43, 0.5';
        tmpCtx.fill();
        tmpCtx.closePath();

        tmpCtx.beginPath();
        tmpCtx.arc(20, 20, 16, 0, 2 * Math.PI);
        tmpCtx.clip();
        tmpCtx.drawImage(base_image, 4, 4, 32, 32);
        resolve(tmpCanvas);
      };
      base_image.onerror = reject;
    });
  };
  const loadImages = async (data) => {
    let images = [];
    for (let i = 0; i < data.length; i++) {
      let item = data[i];
      if (mountedHook.current) {
        let canvas;
        try {
          canvas = await createImageUrl({ imageId: item.imageId, imageUrl: item.imageUrl, party: item.party });
        } catch {
          try {
            canvas = await createImageUrl({
              usePlaceholder: true,
            });
          } catch {}
        }
        images.push(canvas);
      } else {
        return;
      }
    }
    return images;
  };

  const callRelevantStakeholders = async ({
    topic = topicParameter
      ? keywordsListsToUse.find((item) => item.id === parseInt(topicParameter))
      : keywordsListsToUse[0],
    source,
    freeSearch,
  }) => {
    try {
      if (topic && !freeSearch) {
        let pathname = `/api/${
          teamId
            ? 'widget/stakeholder-query-ranked'
            : `${topic?.prospective ? 'search/query' : 'stakeholder/query-ranked'}`
        }`;
        let method = 'post';
        let requestProperties = {
          pageSize: 50,
          filters: [
            { field: 'categories', value: 'MP', operator: 'str_eq' },
            { field: 'categories', value: 'Lord', operator: 'str_eq' },
          ],
          pageNumber: 1,
          sortMode: 'relevance',
        };

        if (topic?.prospective) {
          requestProperties = {
            ...requestProperties,
            type: 'stakeholder',
            query: ChangeKeywordsCategory(topic?.keywords),
            andKeywords: ChangeKeywordsExclusions(topic?.andKeywords),
            mustNotQueries: ChangeKeywordsExclusions(topic?.keywordExclusions),
          };
        } else {
          requestProperties = {
            ...requestProperties,
            keywordListId: topic?.id,
          };
        }

        if (teamId) {
          requestProperties = {
            ...requestProperties,
            teamId,
          };
        }
        let results = await generalApiCall({
          pathname,
          method,
          requestProperties,
          needsAuthentication: true,
          notShowErrorMessage: true,
          requestSource: source,
        });
        if (results) {
          mountedHook.current = true;
          if (results.currentlyProcessing) {
            return { currentlyProcessing: true };
          } else {
            let chartLabels = { x: 'Most interested', y: 'Most influential' };
            let data = results.hits?.map((item) => {
              let { title, summary, positionCategoryRank, totalContributionCount, party } = item;
              let x, y;
              y = positionCategoryRank;
              x = totalContributionCount;
              return {
                ...item,
                y,
                x,
                imageId: item.imageId,
                name: title,
                textBody: summary,
                party: party ? normalize(party) : null,
              };
            });
            let images = await loadImages(data);
            return {
              graphData: data,
              pointInTime: results?.pointInTime,
              images,
              chartLabels,
            };
          }
        }
      }
      if (freeSearch) {
        return { graphData: [], images: [] };
      }
    } catch (error) {}
  };

  return { callRelevantStakeholders };
};

export { partyColours, stateColours, houseColours, stakeholderUrlImage };
export default useCallMostRelevantStakeholders;
